import React, {KeyboardEvent} from 'react';
import {Box, Chip, Stack, useTheme} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {
    PowerStationAlarmSummary,
    PowerStationCommunicationState,
} from '../PerformanceAnalysisReducer';
import {formatOverviewAlarmCount} from '../performanceAnalysisUtils';
import {OverviewAlarmCategory} from '../../Alarms/Components/ChipFilterAlarms/types';
import {navigateToOverviewAlarms} from '../PerformanceAnalysisNavigation';

type AlarmPillConfig = {
    category: OverviewAlarmCategory;
    label: string;
    count: number;
    sx: Record<string, unknown>;
};

const CRITICAL_SX = {
    height: 24,
    borderRadius: '100px',
    paddingLeft: '4px',
    paddingRight: '4px',
    fontFamily: '"Work Sans", sans-serif',
    fontWeight: 600,
    fontSize: 13,
    lineHeight: '18px',
    letterSpacing: '0.16px',
    color: '#FC531F',
    backgroundColor: 'rgba(252, 83, 31, 0.1)',
    '& .MuiChip-label.MuiChip-label': {paddingLeft: '6px', paddingRight: '6px'},
    '&:focus-visible': {outline: '2px solid #2D5DE7', outlineOffset: 2},
};

const NON_CRITICAL_SX = {
    height: 24,
    borderRadius: '100px',
    paddingLeft: '4px',
    paddingRight: '4px',
    fontFamily: '"Work Sans", sans-serif',
    fontWeight: 600,
    fontSize: 13,
    lineHeight: '18px',
    letterSpacing: '0.16px',
    color: '#F57F17',
    backgroundColor: 'rgba(249, 168, 37, 0.1)',
    '& .MuiChip-label.MuiChip-label': {paddingLeft: '6px', paddingRight: '6px'},
    '&:focus-visible': {outline: '2px solid #2D5DE7', outlineOffset: 2},
};

const EQUIPMENT_NO_COMM_SX = {
    height: 24,
    borderRadius: '100px',
    paddingLeft: '4px',
    paddingRight: '4px',
    fontFamily: '"Work Sans", sans-serif',
    fontWeight: 600,
    fontSize: 13,
    lineHeight: '18px',
    letterSpacing: '0.16px',
    color: '#FFFFFF',
    backgroundColor: 'rgba(255, 120, 0, 0.8)',
    '& .MuiChip-label.MuiChip-label': {paddingLeft: '6px', paddingRight: '6px'},
    '&:focus-visible': {outline: '2px solid #2D5DE7', outlineOffset: 2},
};

const PLANT_NO_COMM_SX = {
    height: 'auto',
    minHeight: 44,
    width: 123,
    maxWidth: '100%',
    borderRadius: '100px',
    paddingLeft: '12px',
    paddingRight: '12px',
    fontFamily: '"Work Sans", sans-serif',
    fontWeight: 600,
    fontSize: 13,
    lineHeight: '18px',
    letterSpacing: '0.16px',
    color: '#FFFFFF',
    backgroundColor: 'rgba(23, 23, 23, 0.8)',
    whiteSpace: 'normal',
    '& .MuiChip-label.MuiChip-label': {
        boxSizing: 'border-box',
        display: 'block',
        width: '100%',
        whiteSpace: 'normal',
        textAlign: 'center',
        paddingLeft: '6px',
        paddingRight: '6px',
        paddingTop: '4px',
        paddingBottom: '4px',
    },
};

const SYNCHRONIZING_SX = {
    height: 32,
    borderRadius: '100px',
    paddingTop: '4px',
    paddingBottom: '4px',
    paddingLeft: '12px',
    paddingRight: '12px',
    fontFamily: '"Work Sans", sans-serif',
    fontWeight: 600,
    fontSize: 13,
    lineHeight: '18px',
    letterSpacing: '0.16px',
    color: '#FFFFFF',
    backgroundColor: 'rgba(7, 26, 200, 0.8)',
    '& .MuiChip-label.MuiChip-label': {
        minHeight: 24,
        display: 'flex',
        alignItems: 'center',
        paddingLeft: '6px',
        paddingRight: '6px',
    },
};

export interface PlantAlarmSummaryCellProps {
    powerStationId: number;
    powerStationName: string;
    communicationState: PowerStationCommunicationState;
    alarmSummary: PowerStationAlarmSummary;
}

function buildAccessiblePillLabel(
    displayCount: string,
    categoryLabel: string,
    powerStationName: string
): string {
    return `Abrir ${displayCount} alarmes ${categoryLabel} da usina ${powerStationName}`;
}

function AlarmCategoryPill({
    displayCount,
    categoryLabel,
    powerStationName,
    sx,
    onActivate,
}: {
    displayCount: string;
    categoryLabel: string;
    powerStationName: string;
    sx: Record<string, unknown>;
    onActivate: () => void;
}) {
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onActivate();
        }
    };

    return (
        <Chip
            tabIndex={0}
            role="button"
            aria-label={buildAccessiblePillLabel(displayCount, categoryLabel, powerStationName)}
            label={`${categoryLabel} ${displayCount}`}
            onClick={onActivate}
            onKeyDown={handleKeyDown}
            sx={sx}
        />
    );
}

export function PlantAlarmSummaryCell({
    powerStationId,
    powerStationName,
    communicationState,
    alarmSummary,
}: PlantAlarmSummaryCellProps) {
    const navigate = useNavigate();
    const theme = useTheme();

    const handleNavigate = (category: OverviewAlarmCategory) => {
        navigateToOverviewAlarms(navigate, powerStationId, category);
    };

    if (communicationState === 'NO_COMMUNICATION') {
        return (
            <Chip
                label="Usina sem comunicação"
                aria-label="Usina sem comunicação"
                sx={{
                    ...PLANT_NO_COMM_SX,
                    ...(theme.palette.mode === 'dark'
                        ? {backgroundColor: 'rgba(23, 23, 23, 0.95)'}
                        : {}),
                }}
            />
        );
    }

    if (communicationState === 'SYNCHRONIZING') {
        return (
            <Chip
                label="Sincronizando"
                aria-label="Sincronizando"
                sx={SYNCHRONIZING_SX}
            />
        );
    }

    const pills: AlarmPillConfig[] = [
        {
            category: 'CRITICAL',
            label: 'Críticos',
            count: alarmSummary.critical,
            sx: CRITICAL_SX,
        },
        {
            category: 'NON_CRITICAL',
            label: 'Não Críticos',
            count: alarmSummary.nonCritical,
            sx: NON_CRITICAL_SX,
        },
        {
            category: 'EQUIPMENT_NO_COMMUNICATION',
            label: 'Sem comunicação',
            count: alarmSummary.equipmentNoCommunication,
            sx: EQUIPMENT_NO_COMM_SX,
        },
    ];

    const visiblePills = pills
        .map((pill) => ({...pill, displayCount: formatOverviewAlarmCount(pill.count)}))
        .filter((pill) => pill.displayCount != null);

    if (visiblePills.length === 0) {
        return null;
    }

    return (
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{gap: '4px'}}>
            {visiblePills.map((pill) => (
                <AlarmCategoryPill
                    key={pill.category}
                    displayCount={pill.displayCount as string}
                    categoryLabel={pill.label}
                    powerStationName={powerStationName}
                    sx={pill.sx}
                    onActivate={() => handleNavigate(pill.category)}
                />
            ))}
        </Stack>
    );
}
